import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
    /** @type {MutationObserver | undefined} */
    #gridObserver;

    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, {
            signal: this.#abortController.signal,
        });
        this.setAttribute('initialized', '');
        this.#scrollToHashTarget();

        if (this.classList.contains('js-na-collection')) {
            this.#applyGridOrder();
            this.#observeGridForOrder();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
        this.#gridObserver?.disconnect();
    }

    updatedCallback() {
        if (!this.classList.contains('js-na-collection')) return;
        this.#applyGridOrder();
        this.#observeGridForOrder();
    }

    /**
     * Updates the layout.
     *
     * @param {Event} event
     */
    updateLayout({ target }) {
        if (!(target instanceof HTMLInputElement)) return;

        this.#animateLayoutChange(target.value);
    }

    /**
     * Sets the layout.
     *
     * @param {string} value
     */
    #animateLayoutChange = async (value) => {
        const { grid } = this.refs;

        if (!grid) return;

        await startViewTransition(() => this.#setLayout(value), ['product-grid']);

        requestIdleCallback(() => {
            const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
            sessionStorage.setItem(`product-grid-view-${viewport}`, value);
        });
    };

    /**
     * Animates the layout change.
     *
     * @param {string} value
     */
    #setLayout(value) {
        const { grid } = this.refs;
        if (!grid) return;
        grid.setAttribute('product-grid-view', value);
    }

    #observeGridForOrder() {
        this.#gridObserver?.disconnect();
        const { grid } = this.refs;
        if (!grid) return;
        this.#gridObserver = new MutationObserver(() => this.#applyGridOrder());
        this.#gridObserver.observe(grid, { childList: true });
    }

    #applyGridOrder() {
        const { grid } = this.refs;
        if (!grid) return;

        const fillerCards = grid.querySelectorAll('.fillerObject[data-order]');
        if (!fillerCards.length) return;

        const productCards = grid.querySelectorAll('.product-grid__item[data-product-id]');

        const fillerPositions = Array.from(fillerCards)
            .map((el) => parseInt(/** @type {HTMLElement} */ (el).dataset.order ?? '', 10))
            .filter((n) => !isNaN(n));

        let slot = 1;
        productCards.forEach((card) => {
            while (fillerPositions.includes(slot)) slot++;
            if (card instanceof HTMLElement) card.style.order = String(slot);
            slot++;
        });

        fillerCards.forEach((filler) => {
            const order = parseInt(/** @type {HTMLElement} */ (filler).dataset.order ?? '', 10);
            const item = filler.closest('.product-grid__item') ?? filler;
            if (!isNaN(order) && item instanceof HTMLElement) item.style.order = String(order);
        });
    }

    #scrollToHashTarget() {
        const hash = window.location.hash.slice(1);
        if (!hash) return;

        const card = document.getElementById(hash);
        if (card) {
            card.scrollIntoView({ behavior: 'instant' });
        }
    }

    /**
     * Handles the media query change event.
     *
     * @param {MediaQueryListEvent} event
     */
    #handleMediaQueryChange = (event) => {
        const targetElement = event.matches
            ? this.querySelector('[data-grid-layout="desktop-default-option"]')
            : this.querySelector('[data-grid-layout="mobile-option"]');

        if (!(targetElement instanceof HTMLInputElement)) return;

        targetElement.checked = true;
        this.#setLayout('default');
    };
}

if (!customElements.get('results-list')) {
    customElements.define('results-list', ResultsList);
}
